import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Flex, 
  Stack, 
  Link, 
  Icon, 
  Text, 
  Button, 
  useColorModeValue,
  IconButton,
  useToast,
  Drawer,
  DrawerBody,
  DrawerHeader,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton
} from '@chakra-ui/react';
import { Link as RouterLink, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { FiHome, FiSettings, FiDollarSign, FiLogOut, FiMenu } from 'react-icons/fi';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import { useDevPreview } from '../contexts/DevPreviewContext';

const Layout: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout } = useAuth();
  const queryClient = useQueryClient();
  const { toggleDevPreview } = useDevPreview();
  const toast = useToast();
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 1024);
  
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 1024);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  useEffect(() => {
    if (isMobile && isDrawerOpen) {
      setIsDrawerOpen(false);
    }
  }, [location.pathname, isMobile]);

  const handleLogout = () => {
    logout();
    // Cached data belongs to this partner; don't let the next login see it
    queryClient.clear();
    navigate('/login');
  };

  const isActive = (path: string) => location.pathname === path;

  // Temporary: five clicks on the title within two seconds toggle the
  // Dev mode (see DevPreviewContext)
  const titleClicks = React.useRef<number[]>([]);
  const handleTitleClick = () => {
    const now = Date.now();
    titleClicks.current = [...titleClicks.current.filter((at) => now - at < 2000), now];
    if (titleClicks.current.length < 5) return;
    titleClicks.current = [];
    const on = toggleDevPreview();
    toast({
      title: on ? 'Dev mode enabled' : 'Dev mode disabled',
      status: 'info',
      duration: 3000,
      isClosable: true,
    });
  };

  const NavItem = ({
    to,
    icon,
    children,
    onClick,
  }: {
    to: string;
    icon: any;
    children: React.ReactNode;
    onClick?: () => void;
  }) => (
    <Link
      as={RouterLink}
      to={to}
      display="flex"
      alignItems="center"
      px={4}
      py={3}
      rounded="md"
      bg={isActive(to) ? 'brand.50' : 'transparent'}
      color={isActive(to) ? 'brand.500' : 'gray.600'}
      _hover={{
        bg: 'brand.50',
        color: 'brand.500',
      }}
      onClick={onClick}
    >
      <Icon as={icon} mr={3} />
      <Text fontWeight="medium">{children}</Text>
    </Link>
  );

  const SidebarContent = ({ onItemClick }: { onItemClick?: () => void }) => (
    <Flex direction="column" h="full">
      <Box mb={8}>
        <Text fontSize="2xl" fontWeight="bold" color="brand.500" userSelect="none" onClick={handleTitleClick}>
          Breez Portal
        </Text>
      </Box>

      <Stack spacing={1} flex="1">
        <NavItem to="/dashboard" icon={FiHome} onClick={onItemClick}>
          Dashboard
        </NavItem>
        <NavItem to="/fees" icon={FiDollarSign} onClick={onItemClick}>
          Fees
        </NavItem>
        <NavItem to="/settings" icon={FiSettings} onClick={onItemClick}>
          Settings
        </NavItem>
      </Stack>

      <Button
        variant="ghost"
        leftIcon={<FiLogOut />}
        onClick={() => {
          handleLogout();
          onItemClick?.();
        }}
        justifyContent="flex-start"
        w="full"
      >
        Logout
      </Button>
    </Flex>
  );

  if (isMobile) {
    return (
      <>
        <Box
          position="fixed"
          top={0}
          left={0}
          right={0}
          zIndex={1000}
          bg={bgColor}
          borderBottom="1px"
          borderColor={borderColor}
          px={4}
          py={3}
          boxShadow="sm"
        >
          <Flex align="center" justify="space-between">
            <Text fontSize="xl" fontWeight="bold" color="brand.500" userSelect="none" onClick={handleTitleClick}>
              Breez Portal
            </Text>
            <IconButton
              aria-label="Open menu"
              icon={<FiMenu />}
              variant="ghost"
              onClick={() => setIsDrawerOpen(true)}
              size="md"
            />
          </Flex>
        </Box>

        <Drawer isOpen={isDrawerOpen} placement="left" onClose={() => setIsDrawerOpen(false)} size="sm">
          <DrawerOverlay />
          <DrawerContent>
            <DrawerCloseButton />
            <DrawerBody p={4}>
              <SidebarContent onItemClick={() => setIsDrawerOpen(false)} />
            </DrawerBody>
          </DrawerContent>
        </Drawer>

        <Box minH="100vh" bg="gray.50" pt="100px" px={4} pb={4}>
          <Outlet />
        </Box>
      </>
    );
  }

  return (
    <Flex minH="100vh">
      <Box
        w="64"
        bg={bgColor}
        borderRight="1px"
        borderColor={borderColor}
        py={8}
        px={4}
      >
        <SidebarContent />
      </Box>

      {/* Main Content */}
      <Box flex="1" bg="gray.50" p={8}>
        <Outlet />
      </Box>
    </Flex>
  );
};

export default Layout; 